import React, { useEffect, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { memberApi } from "../../lib/api";
import { useMemberAuth } from "../../context/MemberAuthContext";
import MemberLayout from "../../components/MemberLayout";
import { Calendar, MessageSquare, ArrowRight, Sparkles, User, Megaphone, BarChart3, Pin } from "lucide-react";

const MemberHome = () => {
  const { member, loading } = useMemberAuth();
  const location = useLocation();
  const [data, setData] = useState({ unread_messages: 0, upcoming_events: [], network_updates: [], network_updates_total: 0 });
  useEffect(() => { if (member) memberApi.get("/member/home").then(({ data }) => setData(data)).catch(() => {}); }, [member]);
  if (loading) return <MemberLayout><div className="rounded-lg p-8" style={{ background: '#fbf8ef' }}>Signing you in…</div></MemberLayout>;
  if (!member) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  const first = (member.display_name || 'there').split(' ')[0];
  return (
    <MemberLayout>
      <div className="section-eyebrow text-[#0d2a20]/60 mb-2">Members</div>
      <h1 className="hero-title text-[40px] md:text-[56px] leading-none mb-2">Welcome back, <span className="font-serif-i text-[#e0684d]">{first}</span>.</h1>
      <p className="text-[15px] text-[#0d2a20]/75 mb-8 max-w-[640px]">A quick snapshot of what's happening in your network today.</p>
      {!member.profile_completed && (
        <div className="mb-6 rounded-lg p-5 flex items-center justify-between flex-wrap gap-4" style={{ background: '#fde5c7' }}>
          <div className="flex items-start gap-3"><User size={18} className="text-[#8a4a10] mt-0.5" /><div><div className="font-medium text-[#8a4a10] text-[14px]">Finish setting up your profile</div><div className="text-[13px] text-[#8a4a10]/85">This is how you'll appear to other Vemoye members.</div></div></div>
          <Link to="/member/profile" className="inline-flex items-center gap-2 px-4 py-2 rounded-md btn-coral text-[13px]">Complete profile <ArrowRight size={14} /></Link>
        </div>
      )}

      {(data.network_updates || []).length > 0 && (
        <section className="mb-10" data-testid="member-home-network-updates">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Megaphone size={16} className="text-[#e0684d]" />
              <div className="section-eyebrow text-[#0d2a20]/60">Network Updates</div>
            </div>
            <Link to="/member/updates" className="text-[13px] link-underline inline-flex items-center gap-1">See all {data.network_updates_total ? `(${data.network_updates_total})` : ''} <ArrowRight size={12} /></Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.network_updates.map(u => (
              <Link key={u.id} to="/member/updates" className="rounded-lg p-5 org-card block" style={{ background: '#fbf8ef' }} data-testid={`member-home-nu-${u.id}`}>
                <div className="flex items-center gap-2 mb-3 flex-wrap text-[11px] text-[#0d2a20]/60">
                  {u.pinned && <span className="px-2 py-1 rounded font-medium" style={{ background: '#fde5c7', color: '#8a4a10' }}><Pin size={10} className="inline mr-1" />Pinned</span>}
                  {u.kind === 'poll' && <span className="px-2 py-1 rounded font-medium" style={{ background: '#e8dcc4', color: '#5a4a1a' }}><BarChart3 size={10} className="inline mr-1" />Poll</span>}
                </div>
                <h3 className="text-[16px] font-medium leading-tight mb-2 line-clamp-2">{u.title}</h3>
                {u.image_url && (
                  <div className="mb-2 rounded-md overflow-hidden">
                    <img src={u.image_url.startsWith("http") ? u.image_url : `${process.env.REACT_APP_BACKEND_URL}${u.image_url}`} alt="" className="w-full h-32 object-cover" />
                  </div>
                )}
                <div className="text-[12px] text-[#0d2a20]/60">Read update →</div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <div className="grid md:grid-cols-3 gap-4 mb-10">
        <Link to="/member/messages" className="rounded-lg p-5 org-card" style={{ background: '#fbf8ef' }}>
          <div className="flex items-center justify-between mb-3"><span className="text-[12px] text-[#0d2a20]/60">Unread messages</span><MessageSquare size={16} className="text-[#e0684d]" /></div>
          <div className="text-[32px] font-medium">{data.unread_messages}</div>
        </Link>
        <Link to="/member/directory" className="rounded-lg p-5 org-card" style={{ background: '#fbf8ef' }}>
          <div className="flex items-center justify-between mb-3"><span className="text-[12px] text-[#0d2a20]/60">Directory</span><User size={16} className="text-[#0d2a20]/40" /></div>
          <div className="text-[15px] font-medium mt-2">{member.searchable ? 'Open the directory' : 'You are opted out'}</div>
          <div className="text-[12px] text-[#0d2a20]/60 mt-1">{member.searchable ? 'Discover other members' : 'Enable in your profile to browse'}</div>
        </Link>
        <Link to="/member/opportunities" className="rounded-lg p-5 org-card" style={{ background: '#fbf8ef' }}>
          <div className="flex items-center justify-between mb-3"><span className="text-[12px] text-[#0d2a20]/60">Opportunities</span><Sparkles size={16} className="text-[#0d2a20]/40" /></div>
          <div className="text-[15px] font-medium mt-2">Coming soon</div>
          <div className="text-[12px] text-[#0d2a20]/60 mt-1">Apply your skills to org projects</div>
        </Link>
      </div>
      <div className="section-eyebrow text-[#0d2a20]/60 mb-4">Upcoming events</div>
      <div className="grid md:grid-cols-3 gap-4">
        {data.upcoming_events.map(e => (
          <Link key={e.id} to={`/member/events/${e.id}`} className="rounded-lg p-5 org-card block" style={{ background: '#fbf8ef' }}>
            <div className="flex items-center gap-2 text-[12px] text-[#0d2a20]/60 mb-3">
              <span className="px-2 py-1 rounded flex items-center gap-1" style={{ background: '#ece5d3' }}><Calendar size={12} /> {e.date}</span>
              <span>{e.tag}</span>
            </div>
            <h3 className="text-[18px] font-medium leading-tight mb-2">{e.title}</h3>
            <div className="text-[12px] text-[#0d2a20]/60">{e.registered ? 'Seat reserved' : 'Learn more →'}</div>
          </Link>
        ))}
      </div>
    </MemberLayout>
  );
};

export default MemberHome;
